"use client";

import { sortAlphabeticallyByProp, CasinoGameTypeEnum } from "@finbackoffice/fe-core";
import {
    FC,
    PropsWithChildren,
    createContext,
    useMemo,
    useState,
    useContext,
    useEffect,
    useCallback,
} from "react";
import { ICategorySummaryResponse } from "@finbackoffice/clientbff-client";
import {
    CasinoFavoritesContext,
    CasinoScopesMap,
    ClientBFFContext,
    formatSearchParamsToString,
    isCasinoGameType,
    useSearchParamsQuery,
} from "@finbackoffice/site-core";
import { useCasinoScope, usePathnameWithLocal } from "hooks";
import { CasinoGameTag } from "@finbackoffice/enums";
import { useRouter } from "next/navigation";
import { useCasinoMethods } from "hooks";
import { RouteNames } from "utils/constants";

type ICasinoToolsContext = {
    gameTypes: CasinoGameTypeEnum[];
    providerOptions: ICategorySummaryResponse[];
    handlePlayNow: (gameId: string, isSlots: boolean, onReady?: () => void) => void;
    handlePlayForFun: (gameId: string, isSlots: boolean, onReady?: () => void) => void;
    navigateToGame: (gameId: string, isSlots: boolean, forFun?: boolean | null) => void;
    showEmptyBalanceError: () => void;
    previousUrl: string;
};

export const CasinoToolsContext = createContext<ICasinoToolsContext>(null as any);

export const CasinoToolsProvider: FC<PropsWithChildren> = ({ children }) => {
    const client = useContext(ClientBFFContext);
    const { favoriteGamesState } = useContext(CasinoFavoritesContext);

    const router = useRouter();
    const { pathnameValues, pathname } = usePathnameWithLocal();
    const { query } = useSearchParamsQuery();
    const { handlePlayForFun, handlePlayNow, navigateToGame, showEmptyBalanceError } =
        useCasinoMethods();
    const { scope } = useCasinoScope();

    const [previousUrl, setPreviousUrl] = useState(pathname);
    const [providerOptions, setProviderOptions] = useState<ICategorySummaryResponse[]>([]);

    const { filter, tag, provider } = query;
    const isFavoritesPage = pathnameValues.includes(RouteNames.FAVORITES);

    const isQueryCasinoGameType = useMemo(
        () => isCasinoGameType(filter as CasinoGameTypeEnum),
        [filter],
    );
    const gameTypes = useMemo(() => {
        switch (scope) {
            case RouteNames.SLOTS:
                if (isQueryCasinoGameType) {
                    return [filter as CasinoGameTypeEnum];
                }
                return CasinoScopesMap.slots;

            case RouteNames.LIVE_CASINO:
                if (isQueryCasinoGameType) {
                    if (tag) {
                        return [
                            CasinoGameTypeEnum.LIVE_CASINO,
                            CasinoGameTypeEnum.LIVE_CASINO_TABLE,
                        ];
                    }
                    return [filter as CasinoGameTypeEnum];
                }
                return [CasinoGameTypeEnum.LIVE_CASINO_TABLE];
            default:
                return [];
        }
    }, [scope, isQueryCasinoGameType, filter, tag]);

    useEffect(() => {
        return () => {
            setPreviousUrl(pathname);
        };
    }, [pathname]);

    useEffect(() => {
        const loadProviderOptions = async () => {
            let response = await client.getCasinoGameSummaryByCategory({
                mobile: true,
                types: gameTypes,
                megaways: filter === "megaways",
                bonusbuy: filter === "bonusbuy",
                has_jackpot: filter === "has_jackpot",
                tag: (tag as CasinoGameTag) ?? undefined,
            });

            if (provider && response.find((item) => item.category === provider) === undefined) {
                removeQuery("provider");
            }

            if (isFavoritesPage) {
                const availableCategories = favoriteGamesState.games.map((game) => game.category);
                response = response.filter((item) => availableCategories.includes(item.category));
                response.forEach(
                    (item) =>
                        (item.total_games = availableCategories.filter(
                            (provider) => provider === item.category,
                        ).length),
                );
            }

            setProviderOptions(response.sort((a, b) => sortAlphabeticallyByProp("category")(a, b)));
        };

        async function load() {
            if (gameTypes.length) {
                try {
                    await loadProviderOptions();
                } catch (error) {
                    console.error(error);
                }
            } else {
                setProviderOptions([]);
            }
        }

        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tag, filter, gameTypes]);

    const removeQuery = useCallback(
        (key: string) => {
            const q = { ...query };

            delete q[key];
            router.replace(`${pathname}?${formatSearchParamsToString(q)}`, { scroll: false });
        },
        [pathname, query, router],
    );

    const value = useMemo(
        () => ({
            gameTypes,
            providerOptions,
            handlePlayForFun,
            handlePlayNow,
            navigateToGame,
            showEmptyBalanceError,
            previousUrl,
        }),
        [
            gameTypes,
            providerOptions,
            handlePlayForFun,
            handlePlayNow,
            navigateToGame,
            showEmptyBalanceError,
            previousUrl,
        ],
    );

    return <CasinoToolsContext.Provider value={value}>{children}</CasinoToolsContext.Provider>;
};
